// Room-level state of the session: who we are, mode, phase, scores and the roster lookup the HUD
// builders use. Pushes the match widget to the HUD.

import type { Hud } from "../hud";
import type { HudMode, MatchHudView, MatchPhase as HudPhase, ScoreboardRow, TeamTag } from "../hud/types";
import { GameMode } from "../proto/gunrun/v1/common_pb";
import type { ModeRules } from "../proto/gunrun/v1/common_pb";
import type { RoomSettings, Welcome } from "../proto/gunrun/v1/game_pb";
import { hudMode, hudPhase, teamTag } from "./convert";
import { leaderOf, scoreboardRows, secondsLeft } from "./hudBridge";
import type { RosterEntry, RosterLookup } from "./hudBridge";
import type { RemotePlayers } from "./remote";

export class RoomState {
  myId = -1;
  myName = "";
  myTeam: TeamTag = "none";
  mode: HudMode = "ffa";
  protoMode: GameMode = 0;
  matchSeed = 0;
  rules: ModeRules | null = null;
  roomName = "";
  mapId = "";
  scoreLimit = 0;
  phase: HudPhase = "warmup";
  phaseEndTick = 0;
  scores: [number, number] = [0, 0];
  round: number | null = null;
  /** Latest scoreboard rows, reused in place. */
  rows: ScoreboardRow[] = [];

  /** The local player as a roster entry (`alive` is kept in sync by the session). */
  readonly me: RosterEntry = { name: "", team: "none", alive: false };

  private shown: { phase: HudPhase; s0: number; s1: number; round: number | null; team: TeamTag; limit: number } = {
    phase: "warmup",
    s0: -1,
    s1: -1,
    round: -1,
    team: "none",
    limit: -1,
  };
  private lastMatchPush = 0;

  constructor(
    private readonly hud: Hud,
    private readonly remotes: RemotePlayers,
  ) {}

  /** Roster lookup bound once so it can be passed around without allocation. */
  readonly lookup: RosterLookup = (id) => {
    if (id === this.myId) return this.me;
    return this.remotes.records.get(id) ?? null;
  };

  applyWelcome(w: Welcome): void {
    this.myId = w.yourId;
    this.myName = w.you?.name ?? "";
    this.myTeam = teamTag(w.you?.team ?? 0);
    this.me.name = this.myName;
    this.me.team = this.myTeam;
    this.protoMode = w.mode;
    this.mode = hudMode(w.mode);
    this.matchSeed = w.matchSeed;
    this.rules = w.rules ?? null;
    this.roomName = w.roomName;
    this.mapId = w.map;
    this.scoreLimit = this.limitOf(w.rules ?? w.settings ?? null);
    this.phase = hudPhase(w.phase);
    this.phaseEndTick = w.phaseEndTick;
    this.scores = [0, 0];
    this.round = null;
    this.rows = [];
    this.shown = { phase: "warmup", s0: -1, s1: -1, round: -1, team: "none", limit: -1 };
    this.remotes.setLocal(this.myId, this.myTeam, this.mode === "ffa");
  }

  /** RoomSettings: the host changed the lobby; a mode change re-teams everyone (PlayerUpdated follows). */
  applySettings(s: RoomSettings): void {
    this.roomName = s.name || this.roomName;
    if (s.mode !== this.protoMode && s.mode !== GameMode.UNSPECIFIED) {
      this.protoMode = s.mode;
      this.mode = hudMode(s.mode);
      this.remotes.setLocal(this.myId, this.myTeam, this.mode === "ffa");
    }
    this.scoreLimit = this.limitOf(s);
  }

  /** What the match is played to: points or kills, rounds in Defusal. */
  private limitOf(r: { scoreLimit: number; roundsToWin: number } | null): number {
    if (r === null) return 0;
    return this.mode === "defusal" ? r.roundsToWin : r.scoreLimit;
  }

  setMyTeam(team: TeamTag): void {
    if (team === this.myTeam) return;
    this.myTeam = team;
    this.me.team = team;
    this.remotes.setLocal(this.myId, team, this.mode === "ffa");
  }

  /** Scoreboard message: rebuilds the rows and returns them. */
  setScoreboard(entries: Parameters<typeof scoreboardRows>[0]): ScoreboardRow[] {
    this.rows = scoreboardRows(entries, this.lookup, this.myId, this.rows);
    return this.rows;
  }

  teamScores(): [number, number] | null {
    return this.mode === "ffa" ? null : this.scores;
  }

  /**
   * Pushes the match widget. Cheap enough to call every snapshot: it only talks to the HUD when
   * the content changed or a quarter second has passed (timer text).
   */
  pushMatch(nowTick: number, nowMs: number, force = false): void {
    const left = secondsLeft(this.phaseEndTick, nowTick);
    const same =
      this.shown.phase === this.phase &&
      this.shown.s0 === this.scores[0] &&
      this.shown.s1 === this.scores[1] &&
      this.shown.round === this.round &&
      this.shown.team === this.myTeam &&
      this.shown.limit === this.scoreLimit;
    if (!force && same && nowMs - this.lastMatchPush < 250) return;
    this.shown = { phase: this.phase, s0: this.scores[0], s1: this.scores[1], round: this.round, team: this.myTeam, limit: this.scoreLimit };
    this.lastMatchPush = nowMs;
    const team = this.mode !== "ffa";
    const leader = team ? null : leaderOf(this.rows);
    let myScore: number | null = null;
    for (const r of this.rows) if (r.isMe) myScore = r.score;
    const view: MatchHudView = {
      mode: this.mode,
      phase: this.phase,
      timeLeftSec: left,
      scores: team ? [this.scores[0], this.scores[1]] : null,
      myTeam: this.myTeam,
      round: this.round,
      scoreLimit: this.scoreLimit,
      leaderName: leader ? leader.name : null,
      myScore,
    };
    this.hud.setMatch(view);
  }
}
